import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * 按域名配置备案信息。
 * firmai.cn / firmai.com.cn 均已备案,各自显示对应 ICP 与 公网安备号。
 * gonganIcon 为公安备案徽标,缺省用 /wn.png。
 *
 * 域名匹配规则:hostname 等于 host,或以 .host 结尾(如 www.firmai.cn 命中 firmai.cn)。
 */
const BEIAN_BY_DOMAIN: Record<string, { icp: string; gongan?: string; gonganIcon?: string }> = {
  'firmai.cn': {
    icp: '沪ICP备2026019486号',
    gongan: '沪公网安备31011502406280号',
  },
  'www.firmai.cn': {
    icp: '沪ICP备2026019486号',
    gongan: '沪公网安备31011502406280号',
  },
  'firmai.com.cn': {
    icp: '沪ICP备2026019486号-2',
    gongan: '沪公网安备31011502406515号',
    gonganIcon: '/batb.png',
  },
  'www.firmai.com.cn': {
    icp: '沪ICP备2026019486号-2',
    gongan: '沪公网安备31011502406515号',
    gonganIcon: '/batb.png',
  },
};

function getBeianForHost(hostname: string) {
  const host = hostname.toLowerCase();
  // 精确匹配优先
  if (BEIAN_BY_DOMAIN[host]) return BEIAN_BY_DOMAIN[host];
  // 子域匹配:www.firmai.cn 也命中 firmai.cn 的配置
  for (const key of Object.keys(BEIAN_BY_DOMAIN)) {
    if (host.endsWith('.' + key)) return BEIAN_BY_DOMAIN[key];
  }
  return null;
}

const footerSections = [
  {
    title: '核心业务',
    links: [
      { label: '工信部碳排放申报', href: '/business/carbon-report' },
      { label: '工业AI智能化', href: '/business/industrial-ai' },
      { label: 'CCER合规填报', href: '/business/ccer-cbam' },
      { label: 'CBAM跨境合规', href: '/business/ccer-cbam' },
      { label: '增值配套服务', href: '/business/value-added' },
    ],
  },
  {
    title: '产品中心',
    links: [
      { label: '碳排放申报管理系统', href: '/products' },
      { label: '工业AI智能管控平台', href: '/products' },
      { label: 'CCER/CBAM合规数据系统', href: '/products' },
      { label: '碳台账区块链存证', href: '/products' },
      { label: '工业能耗AI监测', href: '/products' },
    ],
  },
  {
    title: '行业方案',
    links: [
      { label: '钢铁冶金行业', href: '/industry/steel' },
      { label: '水泥建材行业', href: '/industry/cement' },
      { label: '化工行业', href: '/industry/chemical' },
      { label: '外贸出口制造', href: '/industry/export' },
      { label: '工业园区', href: '/industry/park' },
    ],
  },
  {
    title: '资源与支持',
    links: [
      { label: '政策资讯', href: '/news' },
      { label: '服务支持', href: '/support' },
      { label: '关于我们', href: '/about' },
      { label: '联系我们', href: '/contact' },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();

  // 客户端读取当前域名,按配置决定备案号显示内容
  // - firmai.cn / www.firmai.cn → 沪ICP备2026019486号 + 31011502406280
  // - firmai.com.cn / www.firmai.com.cn → 沪ICP备2026019486号-2 + 31011502406515 (徽标 batb.png)
  // - SSR / 未知域名 → 不显示,避免水合不一致
  const beian = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return getBeianForHost(window.location.hostname);
  }, []);

  // ICP 和 公网安备 都缺失才完全隐藏
  const showBeian = !!(beian && (beian.icp || beian.gongan));

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    navigate(href);
  };

  return (
    <footer id="footer" className="relative bg-[#0D1117] pt-16 md:pt-20 pb-8">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-10 mb-12">
          {/* Brand Col */}
          <div className="lg:col-span-2">
            <a
              href="/"
              onClick={(e) => handleNav(e, '/')}
              className="flex items-center gap-3 mb-4 cursor-pointer"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/b5b3c1b8-bbd4-4161-b813-5c9d75b1cee8.png"
                alt="Firmai Logo"
                className="h-8 w-auto"
              />
              <span className="text-white font-display font-bold text-xl">Firmai</span>
            </a>
            <p className="text-firmai-muted text-sm leading-relaxed mb-5">
              专注工业碳合规+工业AI智能化双核心业务，服务制造企业、工业园区、外贸出口企业，打造工业双碳数字化+AI智能化一站式服务平台。
            </p>
            <div className="space-y-2 mb-5">
              <div className="flex items-center gap-2 text-sm text-firmai-text">
                <span className="w-4 h-4 flex items-center justify-center text-firmai-green shrink-0">
                  <i className="ri-phone-line text-xs" />
                </span>
                合作热线：18916035855
              </div>
              <div className="flex items-center gap-2 text-sm text-firmai-text">
                <span className="w-4 h-4 flex items-center justify-center text-firmai-green shrink-0">
                  <i className="ri-map-pin-line text-xs" />
                </span>
                上海市浦东新区浦建路60弄8号201室
              </div>
              <div className="flex items-center gap-2 text-sm text-firmai-text">
                <span className="w-4 h-4 flex items-center justify-center text-firmai-green shrink-0">
                  <i className="ri-mail-line text-xs" />
                </span>
                商务邮箱：info@firmai.cn
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className="w-8 h-8 shrink-0 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-full text-firmai-green"
                title="微信"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-wechat-fill" />
                </span>
              </span>
              <div className="bg-white p-1.5 rounded-md border border-firmai-border">
                <img
                  src="/kf.png"
                  alt="微信二维码"
                  className="block w-20 h-24 object-contain"
                />
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {footerSections.map((section) => (
              <div key={section.title}>
                <h4 className="text-firmai-muted text-xs font-semibold uppercase tracking-wider mb-4">
                  {section.title}
                </h4>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        onClick={(e) => handleNav(e, link.href)}
                        className="text-firmai-text text-sm hover:text-firmai-green transition-colors duration-200 cursor-pointer"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-firmai-border pt-6">
          {/* ICP - Center:按域名动态渲染 */}
          {showBeian && (
            <div className="text-center mb-4 flex items-center justify-center gap-3 flex-wrap">
              {beian?.icp && (
                <a
                  href="https://beian.miit.gov.cn"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-firmai-muted text-xs hover:text-firmai-green transition-colors duration-200"
                >
                  {beian.icp}
                </a>
              )}
              {beian?.gongan && (
                <a
                  href=" "
                  rel="noreferrer"
                  target="_blank"
                  className="text-firmai-muted text-xs hover:text-firmai-green transition-colors duration-200 inline-flex items-center gap-1"
                >
                  <img src={beian?.gonganIcon ?? '/wn.png'} alt="网安备案" className="h-4 w-auto inline-block" />
                  {beian.gongan}
                </a>
              )}
            </div>
          )}

          {/* Bottom Row */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-firmai-muted text-xs">
              &copy; 2026 上海寻御科技有限公司 · 保留所有权利
            </p>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <a href="/privacy" onClick={(e) => handleNav(e, '/privacy')} className="text-firmai-muted hover:text-firmai-green transition-colors cursor-pointer">隐私政策</a>
              <span className="text-firmai-border">|</span>
              <a href="/terms" onClick={(e) => handleNav(e, '/terms')} className="text-firmai-muted hover:text-firmai-green transition-colors cursor-pointer">服务条款</a>
              <span className="text-firmai-border">|</span>
              <a href="/copyright" onClick={(e) => handleNav(e, '/copyright')} className="text-firmai-muted hover:text-firmai-green transition-colors cursor-pointer">版权声明</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
